import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { Link, Navigate } from 'react-router';
import type {
  ActivityEntry,
  ActivityUser,
  AdminAsset,
  AdminProject,
  AdminUser,
  AuditEntry,
  UpdateUserRequest,
} from '@indusketch/shared';
import { INTL_TAG } from '@indusketch/i18n';
import { formatNumber } from '@indusketch/project-model';
import { Dialog } from '../../ui/Dialog.js';
import { Icon, type IconName } from '../../ui/Icon.js';
import { MenuButton } from '../../ui/Menu.js';
import { adminApi, assetsApi } from '../../lib/api/endpoints.js';
import { userMessage } from '../../lib/errors.js';
import { useI18n } from '../../i18n/i18n.js';
import { useAuthStore } from '../auth/authStore.js';
import { confirmAssetDelete, uploadProblem, useFileDrop } from '../projects/AssetManager.js';
import { ActiveBadge } from '../projects/ActiveBadge.js';
import { AssetCard, formatBytes } from '../assets/AssetCard.js';
import { PriceCatalogsTab } from './PriceCatalogsTab.js';
import {
  shortLocation,
  SortHeader,
  SortSelect,
  useAutoRefresh,
  useSort,
  type SortColumn,
} from './tableTools.js';

type Tab = 'users' | 'activity' | 'projects' | 'assets' | 'prices' | 'audit';
const TABS: {
  id: Tab;
  key: 'tabUsers' | 'tabActivity' | 'tabProjects' | 'tabAssets' | 'tabPrices' | 'tabAudit';
  icon: IconName;
}[] = [
  { id: 'users', key: 'tabUsers', icon: 'user' },
  { id: 'activity', key: 'tabActivity', icon: 'eye' },
  { id: 'projects', key: 'tabProjects', icon: 'hall' },
  { id: 'assets', key: 'tabAssets', icon: 'box' },
  { id: 'prices', key: 'tabPrices', icon: 'file' },
  { id: 'audit', key: 'tabAudit', icon: 'history' },
];

/** Admin area. Access control is enforced by the API; the route guard only avoids a dead UI. */
export function AdminPage() {
  const { m } = useI18n();
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>('users');
  const [statsNonce, setStatsNonce] = useState(0);
  if (user?.role !== 'ADMIN') return <Navigate to="/projects" replace />;
  const changed = () => setStatsNonce((n) => n + 1);
  return (
    <div className="page page-wide admin-page">
      <header className="page-header">
        <h1>{m.admin.title}</h1>
        <Link to="/projects" className="btn">
          {m.admin.back}
        </Link>
      </header>
      <AdminStats nonce={statsNonce} />
      <div className="seg admin-tabs" role="tablist" aria-label={m.admin.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`btn${tab === t.id ? ' btn-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <Icon name={t.icon} />
            {m.admin[t.key]}
          </button>
        ))}
      </div>
      {tab === 'users' && <UsersTab selfId={user.id} onChanged={changed} />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'projects' && <ProjectsTab />}
      {tab === 'assets' && <AssetsTab onChanged={changed} />}
      {tab === 'prices' && <PriceCatalogsTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

function useLoad<T>(load: () => Promise<T>, dependency: unknown = null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let active = true;
    load()
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        if (active) setError(userMessage(err));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is triggered via nonce
  }, [nonce, dependency]);
  return { data, setData, error, setError, reload: () => setNonce((n) => n + 1) };
}

/** Quiet background refresh of a loaded table; a failed attempt waits for the next one. */
function useLiveData<T>(load: () => Promise<T>, setData: (data: T) => void) {
  useAutoRefresh(() => {
    load()
      .then(setData)
      .catch(() => undefined);
  });
}

/** Short date and time, as used in the admin tables. */
function useShortDate() {
  const { locale } = useI18n();
  return (iso: string) =>
    new Intl.DateTimeFormat(INTL_TAG[locale], { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
}

/** Key figures above the tabs; refreshed after changes in the tabs. */
function AdminStats({ nonce }: { nonce: number }) {
  const { m } = useI18n();
  const t = m.admin;
  const { data } = useLoad(() => adminApi.stats(), nonce);
  const count = (n: number | undefined) => (n === undefined ? '–' : formatNumber(n, 0));
  const stats: { icon: IconName; label: string; value: string; highlight?: boolean }[] = [
    { icon: 'user', label: t.statUsers, value: count(data?.activeUsers) },
    {
      icon: 'info',
      label: t.statPending,
      value: count(data?.pendingUsers),
      highlight: (data?.pendingUsers ?? 0) > 0,
    },
    { icon: 'hall', label: t.statProjects, value: count(data?.projects) },
    { icon: 'box', label: t.statModels, value: count(data?.models) },
    { icon: 'package', label: t.statStorage, value: data ? formatBytes(data.storageBytes) : '–' },
  ];
  return (
    <div className="stat-grid" data-testid="admin-stats">
      {stats.map((s) => (
        <div key={s.label} className={`stat-card${s.highlight ? ' is-highlight' : ''}`}>
          <span className="stat-icon">
            <Icon name={s.icon} size={18} />
          </span>
          <span className="stat-text">
            <strong className="stat-value">{s.value}</strong>
            <span className="muted stat-label">{s.label}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

const PAGE_SIZE = 50;

/** Long tables show their first rows and grow on request, so thousands of rows stay responsive. */
function usePaged<T>(rows: readonly T[]) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  return {
    visible: rows.slice(0, limit),
    more: rows.length - limit,
    showMore: () => setLimit((l) => l + PAGE_SIZE),
  };
}

function ShowMore({ paged }: { paged: { more: number; showMore: () => void } }) {
  const { m, fmt } = useI18n();
  if (paged.more <= 0) return null;
  return (
    <button type="button" className="btn show-more" onClick={paged.showMore}>
      {fmt(m.admin.showMore, { n: Math.min(PAGE_SIZE, paged.more) })}
    </button>
  );
}

function SearchField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <label className="search-field">
      <Icon name="search" />
      <input
        className="input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      />
    </label>
  );
}

/** Initials with a green dot while the person works in the editor. */
function Avatar({ name, online = false }: { name: string; online?: boolean }) {
  return (
    <span className={`avatar${online ? ' is-online' : ''}`} aria-hidden>
      {initials(name)}
    </span>
  );
}

function UsersTab({ selfId, onChanged }: { selfId: string; onChanged: () => void }) {
  const { m, fmt } = useI18n();
  const t = m.admin;
  const short = useShortDate();
  const { data, error, setError, reload } = useLoad(() => adminApi.users().then((r) => r.users));
  const [q, setQ] = useState('');
  const [secret, setSecret] = useState<{ title: string; email: string; password: string } | null>(
    null,
  );
  const run = async (action: () => Promise<unknown>) => {
    try {
      setError(null);
      await action();
      reload();
      onChanged();
    } catch (err) {
      setError(userMessage(err));
    }
  };
  const update = (u: AdminUser, patch: UpdateUserRequest) =>
    run(() => adminApi.updateUser(u.id, patch));
  const reject = (u: AdminUser) => {
    if (window.confirm(fmt(t.confirmReject, { email: u.email })))
      void run(() => adminApi.rejectUser(u.id));
  };
  const resetPassword = (u: AdminUser) => {
    if (!window.confirm(fmt(t.confirmReset, { email: u.email }))) return;
    void run(async () => {
      const r = await adminApi.resetPassword(u.id);
      setSecret({ title: t.newPassword, email: r.user.email, password: r.password });
    });
  };
  const pending = data?.filter((u) => u.pending) ?? [];
  const needle = q.trim().toLowerCase();
  const columns: Record<
    'name' | 'role' | 'projects' | 'since' | 'lastActive' | 'status',
    SortColumn<AdminUser>
  > = {
    name: { label: t.colName, value: (u) => u.displayName },
    role: { label: t.colRole, value: (u) => (u.role === 'ADMIN' ? t.roleAdmin : t.roleUser) },
    projects: { label: t.colProjects, value: (u) => u.projectCount, descFirst: true },
    since: { label: t.colSince, value: (u) => u.createdAt, descFirst: true },
    lastActive: { label: t.colLastActive, value: (u) => u.lastActiveAt, descFirst: true },
    status: { label: t.colStatus, value: (u) => (u.disabled ? t.disabled : t.active) },
  };
  const { rows: accounts, sorter } = useSort(
    (data ?? []).filter(
      (u) => !u.pending && `${u.displayName} ${u.email}`.toLowerCase().includes(needle),
    ),
    columns,
    { key: 'since', dir: 'desc' },
  );
  const paged = usePaged(accounts);
  return (
    <section className="card block-card" data-testid="admin-users">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {pending.length > 0 && (
        <div className="admin-block" data-testid="pending-users">
          <h2>{fmt(t.pending, { n: pending.length })}</h2>
          <ul className="plain-list pending-list">
            {pending.map((u) => (
              <li key={u.id} data-testid={`pending-${u.email}`}>
                <span>
                  <strong>{u.displayName}</strong> · {u.email}{' '}
                  <span className="muted">· {fmt(t.registered, { date: short(u.createdAt) })}</span>
                </span>
                <span className="table-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void update(u, { approved: true })}
                  >
                    {t.approve}
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => reject(u)}>
                    {t.reject}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="block-head">
        <div className="list-tools">
          <SearchField
            value={q}
            onChange={setQ}
            placeholder={t.filterUsers}
            label={t.filterUsersAria}
          />
          <SortSelect sorter={sorter} />
        </div>
        <CreateUserForm
          onCreated={(email, password) => {
            setSecret({ title: t.accountCreated, email, password });
            reload();
            onChanged();
          }}
          onError={setError}
        />
      </div>
      {secret && (
        <Dialog title={secret.title} onClose={() => setSecret(null)} testId="user-password">
          <div className="form-stack">
            <p>{fmt(t.oneTimePassword, { email: secret.email })}</p>
            <p>
              <code className="secret" data-testid="one-time-password">
                {secret.password}
              </code>
            </p>
            <div className="dialog-actions">
              <button
                type="button"
                className="btn"
                onClick={() => void navigator.clipboard.writeText(secret.password)}
              >
                {m.common.copy}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setSecret(null)}>
                {m.common.done}
              </button>
            </div>
          </div>
        </Dialog>
      )}
      <div className="table-wrap">
        <table className="table table-cards">
          <thead>
            <tr>
              <SortHeader sorter={sorter} column="name" />
              <SortHeader sorter={sorter} column="role" />
              <SortHeader sorter={sorter} column="projects" className="num" />
              <SortHeader sorter={sorter} column="since" />
              <SortHeader sorter={sorter} column="lastActive" />
              <SortHeader sorter={sorter} column="status" />
              <th />
            </tr>
          </thead>
          <tbody>
            {paged.visible.map((u) => (
              <tr key={u.id} data-testid={`user-${u.email}`}>
                <td className="cell-main">
                  <span className="user-cell">
                    <Avatar name={u.displayName} />
                    <span className="user-cell-text">
                      <strong>{u.displayName}</strong>
                      <span className="muted">{u.email}</span>
                    </span>
                  </span>
                </td>
                <td data-label={t.colRole}>
                  <span className={`badge${u.role === 'ADMIN' ? ' badge-info' : ''}`}>
                    {u.role === 'ADMIN' ? t.roleAdmin : t.roleUser}
                  </span>
                </td>
                <td className="num" data-label={t.colProjects}>
                  {u.projectCount}
                </td>
                <td className="muted nowrap" data-label={t.colSince}>
                  {short(u.createdAt)}
                </td>
                <td className="muted nowrap" data-label={t.colLastActive}>
                  {u.lastActiveAt ? short(u.lastActiveAt) : t.never}
                </td>
                <td data-label={t.colStatus}>
                  <span className={`badge ${u.disabled ? 'badge-danger' : 'badge-success'}`}>
                    {u.disabled ? t.disabled : t.active}
                  </span>
                </td>
                <td className="cell-actions">
                  <span className="table-actions">
                    {u.id === selfId ? (
                      <span className="muted">{t.ownAccount}</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`btn${u.disabled ? '' : ' btn-danger'}`}
                          onClick={() => void update(u, { disabled: !u.disabled })}
                        >
                          {u.disabled ? t.unlock : t.lock}
                        </button>
                        <MenuButton
                          label={fmt(t.userActions, { email: u.email })}
                          items={[
                            {
                              label: u.role === 'ADMIN' ? t.makeUser : t.makeAdmin,
                              icon: 'user',
                              onSelect: () =>
                                void update(u, { role: u.role === 'ADMIN' ? 'USER' : 'ADMIN' }),
                            },
                            {
                              label: t.resetPassword,
                              icon: 'lock',
                              onSelect: () => resetPassword(u),
                            },
                          ]}
                        />
                      </>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShowMore paged={paged} />
      {data && accounts.length === 0 && <p className="muted">{m.projects.noMatch}</p>}
    </section>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

function CreateUserForm({
  onCreated,
  onError,
}: {
  onCreated: (email: string, password: string) => void;
  onError: (m: string) => void;
}) {
  const { m } = useI18n();
  const t = m.admin;
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'USER' | 'ADMIN'>('USER');
  const [busy, setBusy] = useState(false);
  if (!open)
    return (
      <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
        {t.addUser}
      </button>
    );
  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminApi.createUser({ email, displayName, role });
      onCreated(r.user.email, r.password);
      setDisplayName('');
      setEmail('');
      setRole('USER');
      setOpen(false);
    } catch (err) {
      onError(userMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t.createUser} onClose={() => setOpen(false)} testId="create-user-dialog">
      <form className="form-stack" onSubmit={(e) => void onSubmit(e)} data-testid="create-user">
        <label className="field">
          <span>{m.common.name}</span>
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="field">
          <span>{m.common.email}</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{t.role}</span>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as 'USER' | 'ADMIN')}
          >
            <option value="USER">{t.roleUser}</option>
            <option value="ADMIN">{t.roleAdmin}</option>
          </select>
        </label>
        <div className="dialog-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t.createAndPassword}
          </button>
          <button type="button" className="btn" onClick={() => setOpen(false)}>
            {m.common.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ProjectsTab() {
  const { m } = useI18n();
  const t = m.admin;
  const short = useShortDate();
  const load = () => adminApi.projects().then((r) => r.projects);
  const { data, setData, error } = useLoad(load);
  useLiveData(load, setData);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const columns: Record<
    'name' | 'owner' | 'revision' | 'size' | 'changed',
    SortColumn<AdminProject>
  > = {
    name: { label: t.colProject, value: (p) => p.name },
    owner: { label: t.colOwner, value: (p) => p.owner.displayName },
    revision: { label: t.colRevision, value: (p) => p.revision, descFirst: true },
    size: { label: t.colSize, value: (p) => p.documentBytes, descFirst: true },
    changed: { label: t.colChanged, value: (p) => p.updatedAt, descFirst: true },
  };
  const { rows, sorter } = useSort(
    (data ?? []).filter((p) =>
      `${p.name} ${p.owner.displayName} ${p.owner.email}`.toLowerCase().includes(needle),
    ),
    columns,
    { key: 'changed', dir: 'desc' },
  );
  const paged = usePaged(rows);
  return (
    <section className="card block-card" data-testid="admin-projects">
      <div className="block-head">
        <div className="list-tools">
          <SearchField
            value={q}
            onChange={setQ}
            placeholder={t.filterProjects}
            label={t.filterProjectsAria}
          />
          <SortSelect sorter={sorter} />
        </div>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="table-wrap">
        <table className="table table-cards">
          <thead>
            <tr>
              <SortHeader sorter={sorter} column="name" />
              <SortHeader sorter={sorter} column="owner" />
              <SortHeader sorter={sorter} column="revision" className="num" />
              <SortHeader sorter={sorter} column="size" className="num" />
              <SortHeader sorter={sorter} column="changed" />
              <th />
            </tr>
          </thead>
          <tbody>
            {paged.visible.map((p) => (
              <tr key={p.id} className={p.activeUsers.length > 0 ? 'is-live' : undefined}>
                <td className="cell-main">
                  <span className="project-cell">
                    <span className="table-thumb" aria-hidden>
                      {p.thumbnailUrl ? (
                        <img src={p.thumbnailUrl} alt="" loading="lazy" />
                      ) : (
                        <Icon name="hall" size={18} />
                      )}
                    </span>
                    <span className="user-cell-text">
                      <strong>{p.name}</strong>
                      <ActiveBadge users={p.activeUsers} variant="compact" />
                    </span>
                  </span>
                </td>
                <td data-label={t.colOwner}>
                  <span className="user-cell-text">
                    <span>{p.owner.displayName}</span>
                    <span className="muted">{p.owner.email}</span>
                  </span>
                </td>
                <td className="num" data-label={t.colRevision}>
                  {p.revision}
                </td>
                <td className="num" data-label={t.colSize}>
                  {formatBytes(p.documentBytes)}
                </td>
                <td className="muted nowrap" data-label={t.colChanged}>
                  {short(p.updatedAt)}
                </td>
                <td className="cell-actions">
                  <span className="table-actions">
                    {/* Admin edits of foreign projects are recorded in the audit log by the API. */}
                    <Link to={`/projects/${p.id}`} className="btn">
                      {m.common.open}
                    </Link>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShowMore paged={paged} />
      {data && rows.length === 0 && <p className="muted">{m.projects.noMatch}</p>}
    </section>
  );
}

function AssetsTab({ onChanged }: { onChanged: () => void }) {
  const { m, fmt } = useI18n();
  const t = m.admin;
  const short = useShortDate();
  const { data, error, setError, reload } = useLoad(() => adminApi.assets().then((r) => r.assets));
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reload();
      onChanged();
    } catch (err) {
      setError(userMessage(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const upload = (file: File) => {
    const problem = uploadProblem(file);
    if (problem) setError(problem);
    else void act(() => adminApi.uploadSystemAsset(file));
  };
  const drop = useFileDrop(upload);
  const system = data?.filter((a) => !a.owner) ?? [];
  const own = data?.filter((a) => a.owner) ?? [];
  const card = (a: AdminAsset) => (
    <AssetCard
      key={a.id}
      asset={a}
      {...(a.owner ? {} : { badge: t.system })}
      footer={
        <span className="muted tile-meta">
          {fmt(t.uploadedBy, {
            name: a.owner ? a.owner.displayName : t.system,
            date: short(a.createdAt),
          })}
        </span>
      }
      actions={[
        {
          label: m.common.delete,
          icon: 'trash',
          danger: true,
          onSelect: () =>
            void act(async () => {
              if (await confirmAssetDelete(a)) await assetsApi.remove(a.id);
            }),
        },
      ]}
    />
  );
  return (
    <section
      className={`card block-card${drop.over ? ' is-drop-target' : ''}`}
      data-testid="admin-assets"
      {...drop.handlers}
    >
      <header className="block-head">
        <h2 className="block-title">
          {t.systemLibrary}
          {data && <span className="count-badge">{system.length}</span>}
        </h2>
        <label className={`btn btn-primary${busy ? ' is-disabled' : ''}`}>
          <Icon name="upload" />
          {busy ? t.uploading : t.uploadSystem}
          <input
            ref={input}
            type="file"
            accept=".glb,model/gltf-binary"
            hidden
            disabled={busy}
            data-testid="system-glb-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
        </label>
      </header>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {data && system.length === 0 && (
        <div className="empty-state">
          <Icon name="box" size={28} />
          <p>{t.noSystemModels}</p>
        </div>
      )}
      {system.length > 0 && <div className="tile-grid">{system.map(card)}</div>}

      <h2 className="block-title">
        {t.userModels}
        {data && <span className="count-badge">{own.length}</span>}
      </h2>
      {data && own.length === 0 && <p className="muted">{t.noUserModels}</p>}
      {own.length > 0 && <div className="tile-grid">{own.map(card)}</div>}
      {drop.over && <div className="drop-hint">{m.assets.dropHint}</div>}
    </section>
  );
}

function useDuration() {
  const t = useI18n().m.admin;
  const { fmt } = useI18n();
  return (minutes: number) => {
    const n = Math.round(minutes);
    return n < 60
      ? fmt(t.minutes, { n })
      : fmt(t.hoursMinutes, { h: Math.floor(n / 60), m: String(n % 60).padStart(2, '0') });
  };
}

const KIND_BADGE: Record<ActivityEntry['kind'], string> = {
  login: 'badge-success',
  logout: '',
  'login-failed': 'badge-danger',
  work: 'badge-info',
};

/** Location shortened to place and country; the full text stays available as tooltip. */
function Place({ location }: { location: string | null }) {
  if (!location) return <span className="muted">–</span>;
  return <span title={location}>{shortLocation(location)}</span>;
}

/** Device with the IP address below it. */
function DeviceCell({ device, ip }: { device: string | null; ip: string | null }) {
  return (
    <span className="cell-stack">
      <span className="nowrap">{device ?? '–'}</span>
      {ip && <span className="muted mono">{ip}</span>}
    </span>
  );
}

/** Sign-ins, failed attempts and working time per user, plus a timeline of the latest entries. */
function ActivityTab() {
  const { m, fmt, locale } = useI18n();
  const t = m.admin;
  const short = useShortDate();
  const duration = useDuration();
  const { data, setData, error } = useLoad(() => adminApi.activity());
  useLiveData(() => adminApi.activity(), setData);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const matches = (...fields: (string | null | undefined)[]) =>
    !needle || fields.some((f) => f?.toLowerCase().includes(needle));
  const userColumns: Record<
    'name' | 'lastActive' | 'device' | 'location' | 'work7' | 'work30' | 'logins' | 'failed',
    SortColumn<ActivityUser>
  > = {
    name: { label: t.colUser, value: (u) => u.displayName },
    lastActive: { label: t.colLastActive, value: (u) => u.lastActiveAt, descFirst: true },
    device: { label: t.colDevice, value: (u) => u.lastDevice },
    location: { label: t.colLocation, value: (u) => u.lastLocation },
    work7: {
      label: `${t.colWorkGroup} ${t.colWork7}`,
      value: (u) => u.workMinutes7,
      descFirst: true,
    },
    work30: {
      label: `${t.colWorkGroup} ${t.colWork30}`,
      value: (u) => u.workMinutes30,
      descFirst: true,
    },
    logins: { label: `${t.colLogins} (${t.colWork30})`, value: (u) => u.logins30, descFirst: true },
    failed: { label: `${t.colFailed} (${t.colWork30})`, value: (u) => u.failed30, descFirst: true },
  };
  const { rows: users, sorter: userSort } = useSort(
    (data?.users ?? []).filter((u) =>
      matches(u.displayName, u.email, u.lastLocation, u.lastIp, u.lastDevice, u.activeProject?.name),
    ),
    userColumns,
    { key: 'lastActive', dir: 'desc' },
  );
  const pagedUsers = usePaged(users);
  const entryUser = (e: ActivityEntry) => e.user?.displayName ?? e.email;
  const entryColumns: Record<
    'time' | 'user' | 'event' | 'device' | 'location',
    SortColumn<ActivityEntry>
  > = {
    time: { label: t.colTime, value: (e) => e.at, descFirst: true },
    user: { label: t.colUser, value: entryUser },
    event: { label: t.colEvent, value: (e) => t.activityKinds[e.kind] },
    device: { label: t.colDevice, value: (e) => e.device },
    location: { label: t.colLocation, value: (e) => e.location },
  };
  const { rows: entries, sorter: entrySort } = useSort(
    (data?.entries ?? []).filter((e) =>
      matches(e.user?.displayName, e.user?.email, e.email, e.location, e.ip, e.device, e.project?.name),
    ),
    entryColumns,
    { key: 'time', dir: 'desc' },
  );
  const paged = usePaged(entries);
  return (
    <section className="card block-card" data-testid="admin-activity">
      <div className="block-head">
        <SearchField
          value={q}
          onChange={setQ}
          placeholder={t.filterActivity}
          label={t.filterActivityAria}
        />
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="block-head">
        <h2 className="block-title">{t.activitySummary}</h2>
        <SortSelect sorter={userSort} />
      </div>
      <div className="table-wrap">
        <table className="table table-cards activity-table">
          <thead>
            <tr>
              <SortHeader sorter={userSort} column="name" rowSpan={2} />
              <SortHeader sorter={userSort} column="lastActive" rowSpan={2} />
              <SortHeader sorter={userSort} column="device" rowSpan={2} />
              <SortHeader sorter={userSort} column="location" rowSpan={2} />
              <th colSpan={2} className="th-group">
                {t.colWorkGroup}
              </th>
              <th colSpan={2} className="th-group">
                {t.col30Group}
              </th>
            </tr>
            <tr>
              <SortHeader sorter={userSort} column="work7" className="num">
                {t.colWork7}
              </SortHeader>
              <SortHeader sorter={userSort} column="work30" className="num">
                {t.colWork30}
              </SortHeader>
              <SortHeader sorter={userSort} column="logins" className="num">
                {t.colLogins}
              </SortHeader>
              <SortHeader sorter={userSort} column="failed" className="num">
                {t.colFailed}
              </SortHeader>
            </tr>
          </thead>
          <tbody>
            {pagedUsers.visible.map((u) => (
              <tr
                key={u.id}
                data-testid={`activity-user-${u.email}`}
                className={u.online ? 'is-live' : undefined}
              >
                <td className="cell-main">
                  <span className="user-cell">
                    <Avatar name={u.displayName} online={u.online} />
                    <span className="user-cell-text">
                      <strong>{u.displayName}</strong>
                      <span className="muted">{u.email}</span>
                      {u.online && (
                        <span className="online-text">
                          {u.activeProject
                            ? fmt(t.activeIn, { project: u.activeProject.name })
                            : t.online}
                        </span>
                      )}
                    </span>
                  </span>
                </td>
                <td className="nowrap" data-label={t.colLastActive}>
                  {u.online ? (
                    <span className="badge badge-success">{t.online}</span>
                  ) : u.lastActiveAt ? (
                    short(u.lastActiveAt)
                  ) : (
                    <span className="muted">{t.never}</span>
                  )}
                </td>
                <td data-label={t.colDevice}>
                  <DeviceCell device={u.lastDevice} ip={u.lastIp} />
                </td>
                <td data-label={t.colLocation}>
                  <Place location={u.lastLocation} />
                </td>
                <td className="num" data-label={userColumns.work7.label}>
                  {u.workMinutes7 > 0 ? duration(u.workMinutes7) : '–'}
                </td>
                <td className="num" data-label={userColumns.work30.label}>
                  {u.workMinutes30 > 0 ? duration(u.workMinutes30) : '–'}
                </td>
                <td className="num" data-label={userColumns.logins.label}>
                  {u.logins30}
                </td>
                <td className="num" data-label={userColumns.failed.label}>
                  {u.failed30 > 0 ? <span className="badge badge-danger">{u.failed30}</span> : 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShowMore paged={pagedUsers} />

      <div className="block-head">
        <h2 className="block-title">{t.activityTimeline}</h2>
        {entries.length > 0 && <SortSelect sorter={entrySort} />}
      </div>
      {data && entries.length === 0 && <p className="muted">{t.activityEmpty}</p>}
      {entries.length > 0 && (
        <div className="table-wrap">
          <table className="table table-cards">
            <thead>
              <tr>
                <SortHeader sorter={entrySort} column="time" />
                <SortHeader sorter={entrySort} column="user" />
                <SortHeader sorter={entrySort} column="event" />
                <SortHeader sorter={entrySort} column="device" />
                <SortHeader sorter={entrySort} column="location" />
              </tr>
            </thead>
            <tbody>
              {paged.visible.map((e) => (
                <tr key={`${e.kind}-${e.id}`}>
                  <td className="muted nowrap" data-label={t.colTime}>
                    {short(e.at)}
                    {e.until && e.kind === 'work'
                      ? ` – ${new Date(e.until).toLocaleTimeString(INTL_TAG[locale], { hour: '2-digit', minute: '2-digit' })}`
                      : ''}
                  </td>
                  <td className="cell-main">
                    <strong>{entryUser(e) ?? '–'}</strong>
                  </td>
                  <td data-label={t.colEvent}>
                    <span className="cell-stack">
                      <span className={`badge ${KIND_BADGE[e.kind]}`}>
                        {t.activityKinds[e.kind]}
                      </span>
                      {e.kind === 'work' && e.durationMinutes !== null && (
                        <span className="muted activity-detail">
                          {e.project
                            ? fmt(t.workIn, {
                                duration: duration(e.durationMinutes),
                                project: e.project.name,
                              })
                            : fmt(t.workPlain, { duration: duration(e.durationMinutes) })}
                        </span>
                      )}
                      {e.kind === 'login-failed' && e.user && e.email && (
                        <span className="muted activity-detail">{e.email}</span>
                      )}
                    </span>
                  </td>
                  <td data-label={t.colDevice}>
                    <DeviceCell device={e.device} ip={e.ip} />
                  </td>
                  <td data-label={t.colLocation}>
                    <Place location={e.location} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ShowMore paged={paged} />
      {data && (
        <p className="muted field-caption">{fmt(t.activityNote, { days: data.retentionDays })}</p>
      )}
    </section>
  );
}

/** Badge colour of an audit action: removals red, additions green. */
function actionTone(action: string): string {
  if (/\.(delete|reject|unshare)$/.test(action)) return ' badge-danger';
  if (/\.(create|system-upload|share)$/.test(action)) return ' badge-success';
  return '';
}

const asText = (value: unknown): string =>
  typeof value === 'string' ? value : value === undefined ? '' : JSON.stringify(value);

/** Readable details of an audit entry instead of raw JSON. */
function useAuditDetails() {
  const { m, fmt } = useI18n();
  const t = m.admin;
  const roleLabel = (role: unknown) => {
    if (role === 'ADMIN') return t.roleAdmin;
    if (role === 'USER') return t.roleUser;
    if (role === 'editor' || role === 'viewer' || role === 'owner') return m.share.access[role];
    return asText(role);
  };
  return (e: AuditEntry): string[] => {
    const out: string[] = [];
    for (const [key, value] of Object.entries(e.metadata)) {
      switch (key) {
        case 'userId':
          out.push(fmt(t.detailWith, { name: e.userName ?? asText(value).slice(0, 8) }));
          break;
        case 'role':
          out.push(fmt(t.detailRole, { role: roleLabel(value) }));
          break;
        case 'baseRevision':
          out.push(fmt(t.detailRevision, { n: asText(value) }));
          break;
        case 'isDefault':
          if (value === true) out.push(t.detailDefault);
          break;
        case 'disabled':
          out.push(value ? t.detailLocked : t.detailUnlocked);
          break;
        case 'approved':
          if (value) out.push(t.detailApproved);
          break;
        case 'name':
          if (value !== e.targetName) out.push(asText(value));
          break;
        default:
          out.push(`${key}: ${asText(value)}`);
      }
    }
    return out;
  };
}

function AuditTab() {
  const { m } = useI18n();
  const t = m.admin;
  const short = useShortDate();
  const details = useAuditDetails();
  const { data, setData, error, setError } = useLoad(() => adminApi.audit());
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const actions: Record<string, string> = t.actions;
  const targetTypes: Record<string, string> = t.targetTypes;
  const actionLabel = (a: string) => actions[a] ?? a;
  const loadOlder = async () => {
    if (!data?.next) return;
    setLoadingMore(true);
    try {
      const page = await adminApi.audit(data.next);
      setData({ entries: [...data.entries, ...page.entries], next: page.next });
    } catch (err) {
      setError(userMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };
  const all = data?.entries ?? [];
  const present = [...new Set(all.map((e) => e.action))].sort((a, b) =>
    actionLabel(a).localeCompare(actionLabel(b)),
  );
  const needle = q.trim().toLowerCase();
  const columns: Record<'time' | 'actor' | 'action' | 'target', SortColumn<AuditEntry>> = {
    time: { label: t.colTime, value: (e) => e.createdAt, descFirst: true },
    actor: { label: t.colActor, value: (e) => e.actor },
    action: { label: t.colAction, value: (e) => actionLabel(e.action) },
    target: { label: t.colTarget, value: (e) => e.targetName },
  };
  const { rows, sorter } = useSort(
    all.filter(
      (e) =>
        (!action || e.action === action) &&
        (!needle ||
          [e.actor, actionLabel(e.action), e.targetName ?? '', e.targetId, ...details(e)]
            .join(' ')
            .toLowerCase()
            .includes(needle)),
    ),
    columns,
    { key: 'time', dir: 'desc' },
  );
  return (
    <section className="card block-card" data-testid="admin-audit">
      <div className="block-head">
        <div className="list-tools">
          <SearchField
            value={q}
            onChange={setQ}
            placeholder={t.filterAudit}
            label={t.filterAuditAria}
          />
          <select
            className="input"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label={t.actionFilter}
          >
            <option value="">{t.allActions}</option>
            {present.map((a) => (
              <option key={a} value={a}>
                {actionLabel(a)}
              </option>
            ))}
          </select>
          <SortSelect sorter={sorter} />
        </div>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {data?.entries.length === 0 && <p className="muted">{t.auditEmpty}</p>}
      {data && data.entries.length > 0 && rows.length === 0 && (
        <p className="muted">{m.projects.noMatch}</p>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table-cards">
            <thead>
              <tr>
                <SortHeader sorter={sorter} column="time" />
                <SortHeader sorter={sorter} column="actor" />
                <SortHeader sorter={sorter} column="action" />
                <SortHeader sorter={sorter} column="target" />
                <th>{t.colDetails}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="muted nowrap" data-label={t.colTime}>
                    {short(e.createdAt)}
                  </td>
                  <td className="cell-main audit-actor">{e.actor}</td>
                  <td data-label={t.colAction}>
                    <span className={`badge${actionTone(e.action)}`}>{actionLabel(e.action)}</span>
                  </td>
                  <td data-label={t.colTarget}>
                    <span className="cell-stack">
                      <span>{e.targetName ?? '–'}</span>
                      <span className="muted audit-target-type">
                        {targetTypes[e.targetType] ?? e.targetType} · {e.targetId.slice(0, 8)}
                      </span>
                    </span>
                  </td>
                  <td data-label={t.colDetails}>
                    <span className="audit-details">
                      {details(e).map((d) => (
                        <span key={d} className="detail-chip">
                          {d}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.next && (
        <button
          type="button"
          className="btn show-more"
          disabled={loadingMore}
          onClick={() => void loadOlder()}
        >
          {t.loadOlder}
        </button>
      )}
    </section>
  );
}
